import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Printer } from 'lucide-react';
import { useState } from 'react';

interface PrintDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (printType: 'own' | 'full') => void;
  userRole: string;
  userName: string;
}

export default function PrintDialog({
  isOpen,
  onClose,
  onPrint,
  userRole,
  userName,
}: PrintDialogProps) {
  const [printType, setPrintType] = useState<'own' | 'full'>('own');

  const handlePrint = () => {
    // Close the dialog first to prevent it from appearing in print
    onClose();
    // Small delay to ensure dialog is closed before printing
    setTimeout(() => {
      onPrint(printType);
    }, 100);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Print Options
          </DialogTitle>
          <DialogDescription>
            Choose what you want to print from the task board.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup
            value={printType}
            onValueChange={value => setPrintType(value as 'own' | 'full')}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="own" id="own" />
              <Label htmlFor="own" className="flex-1">
                <div className="font-medium">My Tasks Only</div>
                <div className="text-sm text-gray-500">
                  Print only tasks assigned to {userName}
                </div>
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <RadioGroupItem value="full" id="full" />
              <Label htmlFor="full" className="flex-1">
                <div className="font-medium">Full Board</div>
                <div className="text-sm text-gray-500">
                  {userRole === 'admin'
                    ? 'Print all tasks across all teams and users'
                    : userRole === 'manager'
                      ? 'Print all tasks in your team'
                      : 'Print all tasks you can view'}
                </div>
              </Label>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handlePrint} className="flex items-center gap-2">
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
